import axios from "axios";
import { toast } from "react-toastify";

// Define base URLs from environment variables
const BASE_URL_1 = process.env.REACT_APP_NODE_APP_URL;
const BASE_URL_2 = process.env.REACT_APP_API_URL;
const BASE_URL_3 = process.env.REACT_APP_FILTER_COURSES_URL;
const BASE_URL_4 = process.env.REACT_APP_CHATBOT_URL;
const BASE_URL_5 = process.env.REACT_APP_CHATBOT_GENIE_URL;

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const createAxiosInstance = (baseURL) => {
  const instance = axios.create({ baseURL });

  // Request Interceptor
  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("authToken");
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }

      config.meta = config.meta || {};
      if (config.meta.showToast === undefined) {
        config.meta.showToast = true;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response Interceptor
  instance.interceptors.response.use(
    (response) => {
      if (response.config?.meta?.showToast && response.data?.message) {
        toast.success(response.data.message);
      }
      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      const { response } = error;

      if (response?.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          // Queue the request while the token is being refreshed
          return new Promise(function (resolve, reject) {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = "Bearer " + token;
              return instance(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const refreshToken = localStorage.getItem("refreshToken");
          if (!refreshToken) throw new Error("No refresh token");

          const refreshResponse = await axios.post(
            `${BASE_URL_1}api/auth/refreshToken`,
            {
              refreshToken,
            }
          );

          const newAccessToken = refreshResponse.data.accessToken;
          const newRefreshToken = refreshResponse.data.refreshToken;

          localStorage.setItem("authToken", newAccessToken);
          localStorage.setItem("refreshToken", newRefreshToken);

          instance.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

          processQueue(null, newAccessToken);
          return instance(originalRequest);
        } catch (err) {
          processQueue(err, null);
          localStorage.removeItem("authToken");
          localStorage.removeItem("refreshToken");
          window.location.href = "/login";
          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      }

      // Other error handlers
      if (error.config?.meta?.showToast !== false) {
        if (response?.data?.message) {
          toast.error(response.data.message);
        } else {
          toast.error("An unexpected error occurred.");
        }
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

// Create instances for all base URLs
export const api1 = createAxiosInstance(BASE_URL_1);
export const api2 = createAxiosInstance(BASE_URL_2);
export const api3 = createAxiosInstance(BASE_URL_3);
export const api4 = createAxiosInstance(BASE_URL_4);
export const api5 = createAxiosInstance(BASE_URL_5);
